import { useForm, useWatch } from 'react-hook-form';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Form from 'storybookConfig/stories/organisms/Form';
import { useAppSelector, useAppDispatch } from 'store/index';
import { AttributeKey } from 'types/models/company';
import { useEffect } from 'react';
import useAlertQueue from 'hooks/useAlertQueue';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import { updateCompany } from 'utils/api/companies';
import { useMutation } from '@tanstack/react-query';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import Alert from 'storybookConfig/stories/cells/Alert';
import type { FormProps } from './types';
import AttributesSelector from 'components/Common/AttributesSelector';
import Flex from '@react-css/flex';
import useSegment from 'hooks/useSegment';

interface FormFields {
  attributes: AttributeKey[];
}

const attributeToAnalyticsMap: Record<AttributeKey, string> = {
  [AttributeKey.MADE_IN_USA]: 'made_in_usa',
  [AttributeKey.ECO_FRIENDLY]: 'eco_friendly',
  [AttributeKey.SOCIAL_GOOD]: 'social_good',
  [AttributeKey.GLUTEN_FREE]: 'gluten_free',
  [AttributeKey.ORGANIC]: 'organic',
  [AttributeKey.PLANT_BASED]: 'plant_based',
  [AttributeKey.VEGAN]: 'vegan',
  [AttributeKey.KOSHER_CERTIFIED]: 'kosher_certified',
  [AttributeKey.WOMAN_OWNED]: 'woman_owned',
  [AttributeKey.LGBTQ_OWNED]: 'lgbtqia',
  [AttributeKey.AAPI_OWNED]: 'aapi_owned',
  [AttributeKey.BIPOC_OWNED]: 'bipoc_owned',
  [AttributeKey.BLACK_OWNED]: 'black_owned',
  [AttributeKey.LATINO_OWNED]: 'latino_owned',
};

const BrandIdentityForm = ({ onNext, onBack, marketplaceData, setMarketplaceData }: FormProps) => {
  const companyId = useAppSelector(selectCompanyId);
  const dispatch = useAppDispatch();
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const analytics = useSegment();
  const { control, handleSubmit, reset, getValues, setValue } = useForm<FormFields>({
    mode: 'onChange',
    values: {
      attributes: (marketplaceData?.attributes || []).filter(
        (attr): attr is AttributeKey => attr !== undefined
      ),
    },
  });

  useEffect(() => {
    reset(marketplaceData);
  }, [marketplaceData, reset]);

  const attributes = useWatch({
    name: 'attributes',
    control,
  });

  const handleSelectAttribute = (key: AttributeKey) => {
    const currentAttributes = getValues('attributes');
    const updatedAttributes = currentAttributes.includes(key)
      ? currentAttributes.filter((attr) => attr !== key)
      : [...currentAttributes, key];

    setValue('attributes', updatedAttributes);
    setMarketplaceData({ attributes: updatedAttributes });
  };

  const updatingCompany = useMutation({
    mutationFn: (data: FormFields) => {
      const updatedData = {
        attributes: data.attributes,
      };
      setMarketplaceData(updatedData);
      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      const analyticsProperties: Record<string, boolean> = {};

      Object.values(attributeToAnalyticsMap).forEach((propertyName) => {
        analyticsProperties[propertyName] = false;
      });

      data.marketplaceSettings.attributes?.forEach((attribute) => {
        const propertyName = attributeToAnalyticsMap[attribute];
        if (propertyName) {
          analyticsProperties[propertyName] = true;
        }
      });

      analytics?.track('Supplier Onboarding Brand Attributes Completed', analyticsProperties);
      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Added Values', 'Your values has been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error: { message: string }) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'values',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => updatingCompany.mutate(data))}>
      <Flex>
        <TertiaryButton
          type="button"
          $removePadding="left"
          onClick={onBack}
          $iconName="navigate_before"
        >
          Back
        </TertiaryButton>
      </Flex>
      <Heading variant="Headings/H3">Brand Identity & Values</Heading>
      {(attributes || []).length >= 3 && (
        <Alert type="info">
          We may review the items you add before they are published on your page.
        </Alert>
      )}
      <Form.FieldGroup>
        <AttributesSelector
          selectedAttributes={attributes || []}
          onAttributeClick={handleSelectAttribute}
        />
      </Form.FieldGroup>
      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={updatingCompany.status === 'loading'}>
          {updatingCompany.status === 'loading' ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default BrandIdentityForm;
